
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrganizationScoping } from './useOrganizationScoping';
import { toast } from '@/hooks/use-toast';
import { OrganizationSetting } from '@/types/organization';

export const useOrganizationSettings = (explicitOrgId?: string) => {
  const [settings, setSettings] = useState<OrganizationSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const { organizationId: scopedOrgId } = useOrganizationScoping();
  
  // Use explicit org ID if provided, otherwise fall back to scoped org ID
  const organizationId = explicitOrgId || scopedOrgId;

  useEffect(() => {
    if (organizationId) {
      fetchSettings();
    } else {
      // No organization ID, so we're done loading (with empty settings)
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const fetchSettings = async () => {
    if (!organizationId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('organization_settings')
        .select('*')
        .eq('org_id', organizationId)
        .order('key');

      if (error) throw error;
      setSettings(data || []);
    } catch (error) {
      console.error('Error fetching organization settings:', error);
      toast({
        title: "Error",
        description: "Failed to load organization settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key: string, value: string, description?: string) => {
    if (!organizationId) return;

    try {
      const { data, error } = await supabase
        .from('organization_settings')
        .upsert({
          org_id: organizationId,
          key,
          value,
          description,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'org_id,key'
        })
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setSettings(prev => {
        const existing = prev.find(s => s.key === key);
        if (existing) {
          return prev.map(s => s.key === key ? { ...s, value, description } : s);
        } else {
          return [...prev, data];
        }
      });

      toast({
        title: "Success",
        description: "Setting updated successfully",
      });
    } catch (error) {
      console.error('Error updating setting:', error);
      toast({
        title: "Error",
        description: "Failed to update setting",
        variant: "destructive",
      });
    }
  };

  const getSetting = (key: string) => {
    return settings.find(s => s.key === key)?.value || '';
  };

  const getSettingWithDefault = (key: string, defaultValue: string) => {
    return settings.find(s => s.key === key)?.value || defaultValue;
  };

  return {
    settings,
    loading,
    updateSetting,
    getSetting,
    getSettingWithDefault,
    refetch: fetchSettings,
  };
};
